"use client"

import { createClient } from "@/shared/lib/supabase/client"
import { getEdgeFunctionUrl } from "@/shared/lib/supabase/functions-url"
import type { GenerationRow } from "@/shared/lib/supabase/types"
import { captureClientException } from "@/shared/lib/monitoring/capture-client"
import {
  clampStreamPercent,
  isNdjsonLikeContentType,
  isTerminalStreamFailure,
  readNdjsonStream,
} from "@/shared/lib/streaming/ndjson"
import { isTosViolationMessage } from "./tos-violation"
import type { GenerationPromptModifiers } from "../store"

export type StreamGenerateParams = {
  prompt: string
  style: string
  aspectRatio?: string
  numImages?: number
  negativePrompt?: string
  guidanceScale?: number
  promptModifiers?: GenerationPromptModifiers
  /** Studio "Character" card: the server adds this character's appearance and LoRA. */
  characterId?: string
}

export type GenerationStreamProgressEvent = {
  type: "progress"
  phase: "preparing" | "rendering" | "enhancing" | "finalizing"
  label: string
  percent: number
  /** Reserved; server does not send vendor or internal details. */
  detail?: string
}

export type GenerationResult = {
  error: string | null
  generation: GenerationRow | null
  tosViolation?: boolean
  /** Which stage blocked the prompt: "pre_check" (keyword) or "model" (LLM). For analytics. */
  tosSource?: string
  /** Coarse violation category for analytics grouping (never user prompt text). */
  tosCategory?: string
  /** HTTP status when the failure came from a non-2xx response. */
  status?: number
  /**
   * True when this layer already reported the underlying error via
   * captureClientException (transport/stream failures). Callers should emit
   * their analytics event + toast but must NOT capture a second exception.
   */
  captured?: boolean
}

type GenerationErrorResponse = {
  error?: string
  success?: boolean
  tos_violation?: boolean
} & Record<string, unknown>

type GenerationJsonResponse = {
  error?: string | null
  success?: boolean
  tos_violation?: boolean
  data?: { generation?: GenerationRow }
} & Record<string, unknown>

type GenerationFailureContext = {
  generationId: string
  pipelinePhase: "safety" | GenerationStreamProgressEvent["phase"]
  failureClass: string
}

const GENERATION_FAILURE_PHASES = new Set<GenerationFailureContext["pipelinePhase"]>([
  "safety",
  "preparing",
  "rendering",
  "enhancing",
  "finalizing",
])

class GenerationPipelineError extends Error {
  override name = "GenerationPipelineError"
}

/** NDJSON / JSON may stringify booleans differently across runtimes. */
function readTosViolationFlag(obj: Record<string, unknown>): boolean {
  const value = obj.tos_violation ?? obj.tosViolation
  return value === true || value === "true" || value === 1
}

/** Optional string field from a response object, coerced defensively. */
function readOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined
}

function readGenerationFailureContext(
  obj: Record<string, unknown>
): GenerationFailureContext | null {
  const generationId = readOptionalString(obj.generation_id)
  const pipelinePhase = readOptionalString(obj.pipeline_phase)
  const failureClass = readOptionalString(obj.failure_class)
  if (
    !generationId ||
    !/^[0-9a-f-]{36}$/i.test(generationId) ||
    !pipelinePhase ||
    !GENERATION_FAILURE_PHASES.has(
      pipelinePhase as GenerationFailureContext["pipelinePhase"]
    ) ||
    !failureClass ||
    !/^[A-Za-z][A-Za-z0-9_.:-]{0,63}$/.test(failureClass)
  ) {
    return null
  }

  return {
    generationId,
    pipelinePhase: pipelinePhase as GenerationFailureContext["pipelinePhase"],
    failureClass,
  }
}

function captureGenerationPipelineFailure(
  context: GenerationFailureContext,
  style: string
): void {
  captureClientException(
    new GenerationPipelineError("Image generation failed. Please try again."),
    {
      scope: "generation",
      extra: {
        style,
        source: "result_error",
        generation_id: context.generationId,
        pipeline_phase: context.pipelinePhase,
        failure_class: context.failureClass,
        $exception_fingerprint: `image-generation:${context.pipelinePhase}:${context.failureClass}`,
      },
    }
  )
}

/**
 * Calls the generation edge function with NDJSON streaming enabled.
 * Invokes onProgress for pipeline updates (opaque phases, generic labels).
 */
export async function streamRequestGeneration(
  params: StreamGenerateParams,
  onProgress: (event: GenerationStreamProgressEvent) => void
): Promise<GenerationResult> {
  const supabase = createClient()
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    return { error: "Not authenticated", generation: null }
  }

  const functionsUrl = getEdgeFunctionUrl("generation")

  let res: Response
  try {
    res = await fetch(functionsUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action: "request",
        stream: true,
        ...params,
      }),
      cache: "no-store",
    })
  } catch (err) {
    captureClientException(err, {
      scope: "client-stream",
      extra: { stream: "generation" },
    })
    return { error: "Network error — please try again", generation: null, captured: true }
  }

  const contentType = res.headers.get("content-type") ?? ""

  if (!res.ok) {
    const err = (await res.json().catch(() => ({
      error: `Backend error (${res.status})`,
      success: false,
      tos_violation: false,
    }))) as GenerationErrorResponse
    const errorMsg = err.error ?? `Request failed (${res.status})`
    const failureContext = readGenerationFailureContext(err)
    if (failureContext) {
      captureGenerationPipelineFailure(failureContext, params.style)
    }
    return {
      error: errorMsg,
      generation: null,
      tosViolation: isTosViolationMessage(errorMsg, readTosViolationFlag(err)),
      tosSource: readOptionalString(err.tos_source),
      tosCategory: readOptionalString(err.tos_category),
      status: res.status,
      ...(failureContext ? { captured: true } : {}),
    }
  }

  if (!isNdjsonLikeContentType(contentType)) {
    const data = (await res.json().catch(() => null)) as GenerationJsonResponse | null
    if (!data) {
      return { error: "Invalid response from generation service", generation: null }
    }
    if (!data.success) {
      const errorMsg = data.error ?? "Generation failed"
      const failureContext = readGenerationFailureContext(data)
      if (failureContext) {
        captureGenerationPipelineFailure(failureContext, params.style)
      }
      return {
        error: errorMsg,
        generation: null,
        tosViolation: isTosViolationMessage(
          errorMsg,
          readTosViolationFlag(data)
        ),
        tosSource: readOptionalString(data.tos_source),
        tosCategory: readOptionalString(data.tos_category),
        ...(failureContext ? { captured: true } : {}),
      }
    }
    return { error: null, generation: data.data?.generation ?? null }
  }

  let finalResult: GenerationResult = {
    error: "Stream ended without a result",
    generation: null,
  }
  let failureContext: GenerationFailureContext | null = null

  const readResult = await readNdjsonStream(res, (obj) => {
    if (obj.type === "progress") {
      onProgress({
        type: "progress",
        phase: (obj.phase as GenerationStreamProgressEvent["phase"]) ?? "rendering",
        label: String(obj.label ?? ""),
        percent: clampStreamPercent(obj.percent),
        detail: obj.detail != null ? String(obj.detail) : undefined,
      })
      return
    }

    if (obj.success === true && obj.data && typeof obj.data === "object") {
      const data = obj.data as { generation?: GenerationRow }
      finalResult = {
        error: null,
        generation: data.generation ?? null,
      }
      return
    }

    if (isTerminalStreamFailure(obj)) {
      const errorMsg = String(obj.error ?? "Generation failed")
      const lineTos = isTosViolationMessage(errorMsg, readTosViolationFlag(obj))
      // Server bug-hardening: never replace a TOS terminal outcome with a later non-TOS line.
      if (finalResult.tosViolation === true && !lineTos) {
        return
      }
      failureContext = readGenerationFailureContext(obj)
      finalResult = {
        error: errorMsg,
        generation: null,
        tosViolation: lineTos,
        tosSource: readOptionalString(obj.tos_source),
        tosCategory: readOptionalString(obj.tos_category),
      }
    }
  })

  if (!readResult.success) {
    if (readResult.reason === "empty_body") {
      return { error: "Empty response body", generation: null }
    }
    captureClientException(readResult.error, {
      scope: "client-stream",
      extra: { stream: "generation", phase: "reader" },
    })
    return { error: "Stream interrupted — please try again", generation: null, captured: true }
  }

  if (finalResult.error && failureContext) {
    captureGenerationPipelineFailure(failureContext, params.style)
    return { ...finalResult, captured: true }
  }

  return finalResult
}
